import { useState, useEffect } from 'react';
import { 
  Minus, Plus, ShoppingCart, Search, Gamepad2, Monitor, Joystick,
  LayoutGrid, List, X, Filter, ChevronRight, ChevronLeft, Trash2,
  ChevronUp, ChevronDown
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import OrderConfirmation from '../components/OrderConfirmation';
import type { Product, OrderItem } from '../types/products';
import type { Visionary } from '../types/visionary';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { sendOrderConfirmations, showToast } from '../utils/notifications';

const PLATFORMS = [
  { name: 'PlayStation 5', color: 'blue', type: 'current' },
  { name: 'PlayStation 4', color: 'blue', type: 'previous' },
  { name: 'Xbox Series X/S', color: 'green', type: 'current' },
  { name: 'Xbox One', color: 'green', type: 'previous' },
  { name: 'Nintendo Switch', color: 'red' },
  { name: 'PC', color: 'black' }
];

// Initialize Stripe
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

const PlatformBadge = ({ platform }: { platform: string }) => {
  const platformConfig = PLATFORMS.find(p => p.name === platform);
  
  if (!platformConfig) return null;
  
  const getBadgeColors = () => {
    switch (platformConfig.color) {
      case 'blue':
        return platformConfig.type === 'current' 
          ? 'bg-blue-500/90 text-white border-blue-400/30' 
          : 'bg-blue-500/70 text-white/90 border-blue-400/20';
      case 'green':
        return platformConfig.type === 'current'
          ? 'bg-green-500/90 text-white border-green-400/30'
          : 'bg-green-500/70 text-white/90 border-green-400/20';
      case 'red':
        return 'bg-red-500/90 text-white border-red-400/30';
      default:
        return 'bg-gray-500/90 text-white border-gray-400/30';
    }
  };

  return (
    <div className={`
      inline-flex px-2 py-0.5 rounded-md text-xs font-medium border backdrop-blur-sm
      shadow-lg transform-gpu transition-all duration-300
      ${getBadgeColors()}
    `}>
      {platform}
    </div>
  );
};

const PaymentModal = ({ 
  isOpen, 
  onClose, 
  clientSecret,
  orderItems,
  visionary,
  pointsToApply,
  onSuccess
}: { 
  isOpen: boolean; 
  onClose: () => void;
  clientSecret: string;
  orderItems: OrderItem[];
  visionary: Visionary | null;
  pointsToApply: number;
  onSuccess: () => void;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    const { error: paymentError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.origin,
      },
      redirect: 'if_required',
    });

    if (paymentError) {
      setError(paymentError.message || 'Payment failed');
      setProcessing(false);
      return;
    }

    if (paymentIntent.status === 'succeeded') {
      onSuccess();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-8 max-w-md w-full mx-4">
        <h2 className="text-2xl font-bold text-white mb-6">Enter Payment Details</h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white/5 rounded-lg p-4">
            <PaymentElement />
          </div>

          {error && (
            <div className="text-red-400 text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={!stripe || processing}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-transparent
              ${processing
                ? 'bg-purple-600/50 text-white/50 cursor-not-allowed'
                : 'bg-purple-600 hover:bg-purple-700 text-white'
              }`}
          >
            {processing ? 'Processing...' : 'Pay Now'}
          </button>
        </form>

        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/50 hover:text-white"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

export default function VisionaryOrder() {
  const { user } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('Games');
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [pointsToApply, setPointsToApply] = useState(0);
  const [applyingPoints, setApplyingPoints] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState<string | null>(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentClientSecret, setPaymentClientSecret] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showQuickView, setShowQuickView] = useState<Product | null>(null);
  const [showCart, setShowCart] = useState(false);
  const [isCartExpanded, setIsCartExpanded] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [showPlatformSelect, setShowPlatformSelect] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [priceRange, setPriceRange] = useState<{ min: number; max: number | null }>({ min: 0, max: null });
  const [cartAnimation, setCartAnimation] = useState(false);

  // Add state for dynamic visionary data that can be refreshed
  const [visionary, setVisionary] = useState<Visionary | null>(
    user?.visionary_details ? JSON.parse(user.visionary_details as string) : null
  );

  // Function to refresh visionary data from database
  const refreshVisionaryData = async () => {
    if (!user?.visionary_details) return;
    
    const currentVisionary = JSON.parse(user.visionary_details as string);
    
    try {
      const { data, error } = await supabase
        .from('visionaries')
        .select('*')
        .eq('membership_number', currentVisionary.membership_number)
        .single();
      
      if (error) {
        console.error('Error refreshing visionary data:', error);
        return;
      }
      
      if (data) {
        console.log('Refreshed visionary data:', data);
        setVisionary(data);
      }
    } catch (error) {
      console.error('Failed to refresh visionary data:', error);
    }
  };

  useEffect(() => {
    fetchProducts();
    
    // Refresh visionary data on component mount to get latest points balance
    if (user?.visionary_details) {
      refreshVisionaryData();
    }
    
    // Add effect to handle window resize
    const handleResize = () => {
      setIsSidebarVisible(window.innerWidth >= 1024); // 1024px is the 'lg' breakpoint
    };

    // Initial check
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Update cart count in navbar
  useEffect(() => {
    const cartCountElement = document.querySelector('.cart-item-count') as HTMLElement;
    if (cartCountElement) {
      cartCountElement.textContent = orderItems.length.toString();
      cartCountElement.style.display = orderItems.length > 0 ? 'flex' : 'none';
    }
  }, [orderItems]);

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('status', 'active') // Only show active products - exclude archived ones
        .gt('stock_quantity', 0); // Only show products in stock - customers shouldn't see out-of-stock items

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  // Add a function to calculate the original total without points discount
  const calculateOriginalTotal = () => {
    return orderItems.reduce(
      (sum, item) => sum + item.product.price * item.quantity,
      0
    );
  };

  const handleApplyPoints = () => {
    if (!visionary) return;
    
    // Use original total without any points discount
    const orderTotalPoints = Math.ceil(calculateOriginalTotal() * 100); // Convert pounds to pence
    const maxAllowedByPercentage = Math.floor(orderTotalPoints * 0.4); // 40% of total
    
    const maxPoints = Math.min(
      visionary.loyalty_points,
      maxAllowedByPercentage
    );
    
    setApplyingPoints(true);
    setPointsToApply(maxPoints);
  };

  const updatePointsToApply = (value: number) => {
    if (!visionary) return;
    
    // Use original total without any points discount
    const orderTotalPoints = Math.ceil(calculateOriginalTotal() * 100); // Convert pounds to pence
    const maxAllowedByPercentage = Math.floor(orderTotalPoints * 0.4); // 40% of total
    
    // The max points is the minimum of: user's available points and 40% of order total
    const maxPoints = Math.min(
      visionary.loyalty_points,
      maxAllowedByPercentage
    );
    
    // Check if the requested value exceeds the 40% limit
    if (value > maxAllowedByPercentage && value <= visionary.loyalty_points) {
      showNotification(`Loyalty points can only be used to reduce the total order cost by a maximum of 40%`);
    }
    
    setPointsToApply(Math.max(0, Math.min(value, maxPoints)));
  };

  const showNotification = (message: string) => {
    showToast(message, 'info');
  };

  const addToOrder = (product: Product) => {
    // Check if product is in stock
    if (product.stock_quantity <= 0) {
      showNotification(`${product.name} is out of stock`);
      return;
    }

    setOrderItems(prev => {
      const existingItem = prev.find(item => item.product.id.toString() === product.id.toString());
      
      if (existingItem) {
        // Check if adding another would exceed available stock
        if (existingItem.quantity >= product.stock_quantity) {
          showNotification(`Cannot add more ${product.name}. Only ${product.stock_quantity} in stock.`);
          return prev;
        }
        
        showNotification(`Added another ${product.name} to cart`);
        return prev.map(item =>
          item.product.id.toString() === product.id.toString()
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      
      showNotification(`${product.name} added to cart`);
      return [...prev, { product, quantity: 1 }];
    });
    
    setCartAnimation(true);
    setTimeout(() => setCartAnimation(false), 1000);
  };

  const removeFromCart = (productId: string | number) => {
    setOrderItems(prev => prev.filter(item => item.product.id.toString() !== productId.toString()));
    showNotification("Item removed from cart");
  };

  const updateQuantity = (productId: string | number, delta: number) => {
    setOrderItems(prev => {
      const updated = prev.map(item => {
        if (item.product.id.toString() === productId.toString()) {
          const newQuantity = Math.max(0, item.quantity + delta);
          
          // Check if new quantity exceeds available stock
          if (delta > 0 && newQuantity > item.product.stock_quantity) {
            showNotification(`Cannot add more ${item.product.name}. Only ${item.product.stock_quantity} in stock.`);
            return item;
          }
          
          return { ...item, quantity: newQuantity };
        }
        return item;
      }).filter(item => item.quantity > 0);
      return updated;
    });
  };

  const calculateTotals = () => {
    let total = orderItems.reduce(
      (sum, item) => sum + item.product.price * item.quantity,
      0
    );
    
    if (applyingPoints && pointsToApply > 0) {
      total = Math.max(0, total - (pointsToApply / 100));
    }
    
    return total;
  };

  const handlePayment = async () => {
    try {
      setIsProcessingPayment(true);
      
      localStorage.setItem('cartData', JSON.stringify({
        orderItems,
        visionary,
        pointsToApply
      }));
      
      const { data: paymentData, error: paymentError } = await supabase.functions.invoke('create-payment-intent', {
        body: {
          items: orderItems.map(item => ({
            name: item.product.name,
            quantity: item.quantity,
            price: item.product.price
          }))
        }
      });

      if (paymentError) throw paymentError;
      if (!paymentData?.clientSecret) throw new Error('No client secret returned');

      setPaymentClientSecret(paymentData.clientSecret);
      setShowPaymentModal(true);
      
    } catch (error) {
      console.error('Payment error:', error);
      alert(error instanceof Error ? error.message : 'Payment failed. Please try again.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handlePaymentSuccess = async () => {
    try {
      const total = calculateTotals();
      const pointsEarned = Math.floor(total);
      
      // Get staff ID from user details - staff_details is already a JSON string
      const staffDetails = user?.staff_details ? JSON.parse(user.staff_details) : null;
      
      const { data: orderData, error: orderError } = await supabase
        .rpc('place_order', {
          p_visionary_id: visionary?.membership_number || null,
          p_staff_id: staffDetails?.id || null,
          p_total_amount: total,
          p_points_used: pointsToApply || 0,
          p_points_earned: pointsEarned,
          p_items: orderItems.map(item => ({
            product_id: item.product.id,
            quantity: item.quantity,
            price: item.product.price
          }))
        });

      if (orderError) {
        console.error('Order error:', orderError);
        throw orderError;
      }

      // Update visionary points with improved error handling
      if (visionary) {
        const pointsChange = pointsEarned - pointsToApply;
        console.log(`Updating points for visionary ${visionary.membership_number}: change = ${pointsChange} (earned: ${pointsEarned}, used: ${pointsToApply})`);
        
        const { data: pointsResult, error: pointsError } = await supabase.rpc('update_visionary_points', {
          p_membership_number: visionary.membership_number,
          p_points_change: pointsChange
        });

        if (pointsError) {
          console.error('Error updating visionary points:', pointsError);
          // Don't throw here - order was successful, just log the points issue
        } else if (pointsResult === false) {
          console.error('Points update returned false - visionary may not exist');
        } else {
          console.log('Points updated successfully');
          // Refresh visionary data to show updated points in UI
          await refreshVisionaryData();
        }
      }

      // orderData is now a string (invoice ID)
      setCurrentOrderId(orderData as string);
      setShowConfirmation(true);
      setShowPaymentModal(false);
      localStorage.removeItem('cartData');
      
      // Send notifications (SMS and Email)
      try {
        await sendOrderConfirmations(
          orderData as string,
          total,
          orderItems,
          visionary,
          staffDetails?.name,
          staffDetails?.id,
          pointsToApply,
          pointsEarned
        );
      } catch (error) {
        console.error('Failed to send notifications:', error);
        // We don't throw here to avoid disrupting the order confirmation flow
      }
      
    } catch (error) {
      console.error('Error creating order:', error);
      alert('Payment successful but order creation failed. Please contact support.');
    }
  };

  const productsByCategory = products.reduce((acc, product) => {
    const category = product.category;
    if (!acc[category]) acc[category] = [];
    
    if ((category === 'Games' || category === 'Peripherals') && selectedPlatform) {
      if (product.platform === selectedPlatform) {
        acc[category].push(product);
      }
    } else {
      acc[category].push(product);
    }
    
    return acc;
  }, {} as Record<string, Product[]>);

  const filteredProducts = (products: Product[]) => {
    return products.filter(product => {
      // Search filter
      const matchesSearch = !searchQuery || 
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description.toLowerCase().includes(searchQuery.toLowerCase());

      // Platform filter
      const matchesPlatform = !selectedPlatform || product.platform === selectedPlatform;

      // Price filter
      const matchesPrice = (
        product.price >= priceRange.min &&
        (!priceRange.max || product.price <= priceRange.max)
      );

      return matchesSearch && matchesPlatform && matchesPrice;
    });
  };

  if (loading) {
    return (
      <div className="fixed inset-x-0 bottom-0 top-16 flex items-center justify-center">
        <div className="text-white/80 animate-pulse flex items-center gap-3">
          <div className="w-6 h-6 border-2 border-white/80 border-t-transparent rounded-full animate-spin" />
          Loading products...
        </div>
      </div>
    );
  }

  const total = calculateTotals();

  // Add a function to check if any item in the cart is out of stock
  const checkCartStockAvailability = () => {
    return orderItems.some(item => item.quantity > item.product.stock_quantity);
  };

  return (
    <>
      {/* Move Alert Notification to left side to avoid overlap */}
      <div className={`fixed top-20 left-4 z-50 transition-all duration-300 transform ${
        showAlert ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'
      }`}>
        <div className="bg-purple-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2">
          <ShoppingCart className="w-4 h-4" />
          <span>{alertMessage}</span>
        </div>
      </div>

      {/* Premium Background Design */}
      <div className="fixed inset-0 z-0">
        {/* Base gradient layer */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-950 via-purple-900/40 to-indigo-950" />
        
        {/* Animated mesh gradient overlay */}
        <div className="absolute inset-0 opacity-70">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_0%_0%,rgba(168,85,247,0.2),transparent_50%)] animate-[meshGradient_15s_ease-in-out_infinite]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_100%_0%,rgba(139,92,246,0.2),transparent_50%)] animate-[meshGradient_20s_ease-in-out_infinite]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_100%_100%,rgba(147,51,234,0.2),transparent_50%)] animate-[meshGradient_25s_ease-in-out_infinite]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_0%_100%,rgba(168,85,247,0.2),transparent_50%)] animate-[meshGradient_30s_ease-in-out_infinite]" />
        </div>

        {/* Floating orbs */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-purple-600/20 rounded-full blur-3xl animate-[subtleFloat_20s_ease-in-out_infinite]" />
          <div className="absolute top-3/4 right-1/4 w-[500px] h-[500px] bg-indigo-600/20 rounded-full blur-3xl animate-[subtleFloat_25s_ease-in-out_infinite]" />
          <div className="absolute top-1/2 left-2/3 w-[400px] h-[400px] bg-violet-600/20 rounded-full blur-3xl animate-[subtleFloat_30s_ease-in-out_infinite]" />
        </div>

        {/* Glass effect overlay */}
        <div className="absolute inset-0 backdrop-blur-[120px]" />
        
        {/* Subtle noise texture */}
        <div className="absolute inset-0 opacity-[0.02] mix-blend-overlay">
          <div className="absolute inset-0" style={{ 
            backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\'/%3E%3C/svg%3E")',
            width: '200%',
            height: '200%',
            transform: 'translate(-50%, -50%)'
          }} />
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 top-16 flex">
        {/* Browse Section Toggle Button - Only visible when sidebar is hidden */}
        {!isSidebarVisible && (
          <button
            onClick={() => setIsSidebarVisible(true)}
            className="absolute left-4 top-4 z-20 p-2 bg-white/10 hover:bg-white/20 backdrop-blur-xl border border-white/20 rounded-lg transition-colors"
          >
            <Filter className="w-5 h-5 text-white" />
          </button>
        )}

        {/* Left Panel - Categories & Filters */}
        <div className="fixed lg:relative left-0 h-full w-20 border-r border-white/10 flex flex-col bg-gradient-to-b from-white/[0.07] to-white/[0.05] z-10">
          {/* Sidebar Header */}
          <div className="h-[73px] flex items-center justify-center border-b border-white/10">
            <Filter className="w-5 h-5 text-purple-400" />
          </div>

          {/* Categories */}
          <div className="flex-1 py-4 overflow-y-auto scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent hover:scrollbar-thumb-white/30">
            <div className="space-y-6">
              <div className="space-y-1 px-2">
                {['Games', 'Consoles', 'Peripherals'].map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`w-full aspect-square rounded-lg transition-all duration-300 flex flex-col items-center justify-center gap-2 group
                      ${selectedCategory === category
                        ? 'bg-purple-600 text-white shadow-lg shadow-purple-900/30'
                        : 'text-white/70 hover:text-white hover:bg-white/[0.07]'
                      }`}
                  >
                    <div className={`transition-transform duration-300 ${
                      selectedCategory === category ? 'scale-110' : 'group-hover:scale-110'
                    }`}>
                      {category === 'Games' && <Gamepad2 className="w-5 h-5" />}
                      {category === 'Consoles' && <Monitor className="w-5 h-5" />}
                      {category === 'Peripherals' && <Joystick className="w-5 h-5" />}
                    </div>
                    <span className="text-[10px] font-medium">{category}</span>
                  </button>
                ))}
              </div>

              {/* Replace Platform Filter Button with Filters Button */}
              <div className="px-2">
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowFilters(prev => !prev);
                    }}
                    className={`w-full aspect-square rounded-lg transition-all duration-300 flex flex-col items-center justify-center gap-2 group
                      ${(selectedPlatform || priceRange.max) ? 'bg-white/10 text-white' : 'text-white/70 hover:text-white hover:bg-white/[0.07]'}
                    `}
                  >
                    <div className="transition-transform duration-300 group-hover:scale-110">
                      <Filter className="w-5 h-5" />
                    </div>
                    <span className="text-[10px] font-medium">Filters</span>
                    {(selectedPlatform || priceRange.max) && (
                      <div className="absolute -top-1 -right-1 w-2 h-2 bg-purple-500 rounded-full" />
                    )}
                  </button>
                </div>
              </div>

              {/* New Filters Modal Implementation */}
              {showFilters && (
                <div className="fixed inset-0 z-[9999]">
                  {/* Backdrop */}
                  <div 
                    className="absolute inset-0 bg-black/30 backdrop-blur-sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowFilters(false);
                    }}
                  />
                  
                  {/* Modal */}
                  <div 
                    className="absolute left-24 top-[73px] w-72 bg-white/[0.02] backdrop-blur-2xl border border-white/10 rounded-2xl shadow-2xl"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <div className="p-6 space-y-8">
                      {/* Platform Section - Only show for Games and Peripherals */}
                      {(selectedCategory === 'Games' || selectedCategory === 'Peripherals') && (
                        <div className="space-y-4">
                          <h3 className="text-white/90 font-medium">Platform</h3>
                          <div className="grid grid-cols-1 gap-2">
                            {PLATFORMS.map(platform => {
                              const getButtonStyles = () => {
                                const isSelected = selectedPlatform === platform.name;
                                const baseStyles = "w-full h-12 rounded-xl flex items-center justify-center px-4 backdrop-blur-xl border transition-all duration-200";
                                
                                if (isSelected) {
                                  switch (platform.color) {
                                    case 'blue':
                                      return `${baseStyles} bg-blue-500/20 border-blue-500/30 text-blue-400`;
                                    case 'green':
                                      return `${baseStyles} bg-green-500/20 border-green-500/30 text-green-400`;
                                    case 'red':
                                      return `${baseStyles} bg-red-500/20 border-red-500/30 text-red-400`;
                                    default:
                                      return `${baseStyles} bg-gray-500/20 border-gray-500/30 text-gray-400`;
                                  }
                                } else {
                                  switch (platform.color) {
                                    case 'blue':
                                      return `${baseStyles} bg-blue-500/10 border-blue-500/20 text-blue-400/90 hover:bg-blue-500/20 hover:border-blue-500/30`;
                                    case 'green':
                                      return `${baseStyles} bg-green-500/10 border-green-500/20 text-green-400/90 hover:bg-green-500/20 hover:border-green-500/30`;
                                    case 'red':
                                      return `${baseStyles} bg-red-500/10 border-red-500/20 text-red-400/90 hover:bg-red-500/20 hover:border-red-500/30`;
                                    default:
                                      return `${baseStyles} bg-gray-500/10 border-gray-500/20 text-gray-400/90 hover:bg-gray-500/20 hover:border-gray-500/30`;
                                  }
                                }
                              };

                              return (
                                <button
                                  key={platform.name}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSelectedPlatform(selectedPlatform === platform.name ? null : platform.name);
                                  }}
                                  className={getButtonStyles()}
                                >
                                  <span className="font-medium">
                                    {platform.name}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Price Range Section */}
                      <div className="space-y-4">
                        <h3 className="text-white/90 font-medium">Price Range</h3>
                        <div className="space-y-4">
                          <div className="flex items-center gap-3">
                            <div className="relative flex-1">
                              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40">£</span>
                              <input
                                type="number"
                                value={priceRange.min}
                                onChange={(e) => setPriceRange(prev => ({ ...prev, min: Number(e.target.value) }))}
                                placeholder="Min"
                                className="w-full pl-8 pr-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white text-sm
                                  focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500/30
                                  placeholder:text-white/30 transition-all duration-200"
                              />
                            </div>
                            <div className="relative flex-1">
                              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40">£</span>
                              <input
                                type="number"
                                value={priceRange.max || ''}
                                onChange={(e) => setPriceRange(prev => ({ ...prev, max: e.target.value ? Number(e.target.value) : null }))}
                                placeholder="Max"
                                className="w-full pl-8 pr-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white text-sm
                                  focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500/30
                                  placeholder:text-white/30 transition-all duration-200"
                              />
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setPriceRange({ min: 0, max: null });
                            }}
                            className="w-full px-4 py-3 text-sm text-white/70 hover:text-white hover:bg-white/[0.02] rounded-xl border border-white/[0.02] hover:border-white/10 transition-all duration-200"
                          >
                            Reset Price Filter
                          </button>
                        </div>
                      </div>

                      {/* Apply/Clear Filters */}
                      <div className="space-y-3 pt-2">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowFilters(false);
                          }}
                          className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 
                            text-white text-sm rounded-xl transition-all duration-300 relative group overflow-hidden"
                        >
                          {/* Button shine effect */}
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                          <span className="relative">Apply Filters</span>
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedPlatform(null);
                            setPriceRange({ min: 0, max: null });
                            setShowFilters(false);
                          }}
                          className="w-full px-4 py-3 bg-white/[0.02] hover:bg-white/[0.04] text-white text-sm rounded-xl border border-white/[0.02] hover:border-white/10 transition-all duration-200"
                        >
                          Clear All Filters
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Center Panel - Products */}
        <div className="flex-1 flex flex-col min-w-0 lg:pl-20 transition-all duration-300">
          {/* Header */}
          <div className="h-[73px] flex items-center px-6 border-b border-white/10">
            <div className="flex items-center gap-6 w-full">
              <h1 className="text-xl font-semibold text-white shrink-0">
                {selectedCategory}
                {selectedPlatform && (
                  <span className="text-white/50 ml-2 text-sm">• {selectedPlatform}</span>
                )}
              </h1>

              {/* Search Bar */}
              <div className="flex-1 relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-xl blur opacity-0 group-hover:opacity-100 transition-all duration-500" />
                <div className="relative flex items-center">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/50 group-hover:text-white/70 transition-colors duration-200" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search products by name, description..."
                    className="w-full pl-12 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/50 
                      focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:bg-white/10 focus:border-white/20
                      hover:bg-white/[0.07] hover:border-white/20
                      transform-gpu transition-all duration-200"
                  />
                </div>
              </div>

              {/* Cart Button */}
              <div className="relative">
                <button
                  onClick={() => setIsCartExpanded(!isCartExpanded)}
                  className={`px-4 py-2.5 bg-purple-600/20 hover:bg-purple-600/30 backdrop-blur-xl border border-purple-500/30 rounded-xl flex items-center gap-3 transition-all duration-200 shadow-lg shadow-purple-900/20 ${
                    cartAnimation ? 'animate-cart-update' : ''
                  }`}
                >
                  <div className="relative">
                    <ShoppingCart className="w-5 h-5 text-purple-100" />
                    {orderItems.length > 0 && (
                      <div className="absolute -top-2 -right-2 w-4 h-4 bg-purple-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-medium">{orderItems.length}</span>
                      </div>
                    )}
                  </div>
                  <span className="text-purple-100 font-medium">£{total.toFixed(2)}</span>
                </button>

                {/* Cart Dropdown */}
                {isCartExpanded && (
                  <div className="fixed right-6 top-[129px] w-[400px] bg-black/40 backdrop-blur-xl border border-white/20 rounded-xl shadow-lg transform-gpu z-50">
                    <div className="flex flex-col max-h-[600px]">
                {/* Points Section */}
                {visionary && (
                        <div className="p-4 border-b border-white/10">
                  <div className="bg-white/[0.04] backdrop-blur-xl rounded-lg p-4 space-y-3 border border-white/10">
                    <div className="flex justify-between items-center text-white">
                      <span className="text-white/70">Points Available</span>
                      <span className="font-medium">{visionary.loyalty_points.toLocaleString()}</span>
                    </div>
                    {!applyingPoints && visionary.loyalty_points > 0 && (
                      <button
                        onClick={handleApplyPoints}
                        className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg transition-colors"
                      >
                        Apply Points
                      </button>
                    )}
                    {applyingPoints && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={pointsToApply}
                            onChange={(e) => updatePointsToApply(parseInt(e.target.value))}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all duration-200"
                          />
                          <button
                            onClick={() => {
                              setApplyingPoints(false);
                              setPointsToApply(0);
                            }}
                            className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white text-sm rounded-lg transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                        <p className="text-purple-400 text-sm">
                          Discount: -£{(pointsToApply / 100).toFixed(2)}
                        </p>
                      </div>
                    )}
                          </div>
                  </div>
                )}

                      {/* Cart Items - Scrollable */}
                      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent hover:scrollbar-thumb-white/30">
                        <div className="p-4 space-y-3">
                  {orderItems.map(({ product, quantity }) => (
                    <div
                      key={product.id}
                              className={`group backdrop-blur-xl rounded-lg overflow-hidden transition-all duration-200 border p-3 ${
                                quantity > product.stock_quantity 
                                  ? 'bg-red-500/10 border-red-500/30' 
                                  : 'bg-white/[0.04] hover:bg-white/[0.08] border-white/10'
                              }`}
                    >
                              <div className="flex gap-3">
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                                    <h4 className="text-white font-medium text-sm">{product.name}</h4>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeFromCart(product.id);
                              }}
                                      className="p-1 text-white/50 hover:text-red-400 hover:bg-white/10 rounded transition-colors"
                              title="Remove item"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                                  <div className="flex items-center justify-between mt-2">
                                    <p className="text-white/70 text-sm">£{product.price.toFixed(2)}</p>
                            <div className="flex items-center gap-2 bg-white/5 rounded-lg p-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateQuantity(product.id, -1);
                                }}
                                className="p-1 hover:bg-white/10 rounded transition-colors"
                              >
                                        <Minus className="w-3 h-3 text-white" />
                              </button>
                                      <span className="text-white min-w-[2ch] text-center text-sm">
                                {quantity}
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateQuantity(product.id, 1);
                                }}
                                className="p-1 hover:bg-white/10 rounded transition-colors"
                              >
                                        <Plus className="w-3 h-3 text-white" />
                              </button>
                            </div>
                          </div>
                          {quantity > product.stock_quantity && (
                            <div className="mt-1 text-xs text-red-400 font-medium">
                              Only {product.stock_quantity} in stock!
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                          {/* Empty Cart State */}
                          {orderItems.length === 0 && (
                            <div className="text-center py-8">
                              <p className="text-white/50 text-sm">Your cart is empty</p>
                </div>
                          )}
              </div>
            </div>

                      {/* Cart Total and Checkout - Fixed at bottom */}
                      {orderItems.length > 0 && (
                        <div className="border-t border-white/10 p-4 space-y-4 bg-white/[0.02]">
              {applyingPoints && pointsToApply > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-white/70">Points Applied</span>
                  <span className="text-purple-400">-£{(pointsToApply / 100).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between items-center">
                            <span className="text-white/70">Total</span>
                            <span className="text-lg text-white font-bold">£{total.toFixed(2)}</span>
              </div>
              {visionary && (
                <div className="text-purple-400 text-sm text-right">
                  Points to earn: {Math.floor(total)}
                </div>
              )}
              {checkCartStockAvailability() && (
                <div className="p-2 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-400 text-center">
                  Some items are out of stock or have insufficient stock
                </div>
              )}
              <button
                onClick={handlePayment}
                            disabled={isProcessingPayment || checkCartStockAvailability()}
                            className={`w-full py-2 px-4 text-white text-sm rounded-lg transition-colors ${
                              isProcessingPayment || checkCartStockAvailability()
                                ? 'bg-gray-600/50 cursor-not-allowed'
                                : 'bg-purple-600 hover:bg-purple-700'
                            }`}
                          >
                            {isProcessingPayment ? 'Processing...' : 'Checkout'}
              </button>
                  </div>
                )}
              </div>
            </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'grid'
                      ? 'bg-white/10 text-white'
                      : 'text-white/50 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <LayoutGrid className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'list'
                      ? 'bg-white/10 text-white'
                      : 'text-white/50 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <List className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Products Grid/List */}
          <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent hover:scrollbar-thumb-white/30 p-6">
            <div className={`
              ${viewMode === 'grid' 
                ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6' 
              : 'space-y-4'
              }
              w-full
            `}>
              {filteredProducts(productsByCategory[selectedCategory] || []).map(product => (
                <div
                  key={product.id}
                  onClick={() => setShowQuickView(product)}
                  className={`group relative isolate cursor-pointer ${
                    viewMode === 'grid'
                      ? 'flex flex-col bg-gradient-to-b from-white/[0.04] to-white/[0.02] rounded-xl overflow-hidden hover:from-white/[0.06] hover:to-white/[0.03] transition-all duration-300 hover:-translate-y-1 shadow-[0_8px_16px_-6px_rgba(147,51,234,0.3)] hover:shadow-[0_16px_32px_-4px_rgba(147,51,234,0.5)] border border-white/[0.075] hover:border-purple-500/20'
                      : 'bg-gradient-to-r from-white/[0.04] to-white/[0.02] rounded-xl p-4 flex items-center gap-4 hover:from-white/[0.06] hover:to-white/[0.03] transition-all duration-300 shadow-[0_4px_12px_-4px_rgba(147,51,234,0.3)] hover:shadow-[0_8px_24px_-4px_rgba(147,51,234,0.5)] border border-white/[0.075] hover:border-purple-500/20'
                  }`}
                >
                  {viewMode === 'grid' ? (
                    <>
                      <div className="relative aspect-[4/3] overflow-hidden group-hover:after:opacity-100 after:absolute after:inset-0 after:opacity-0 after:transition-opacity after:duration-300 after:pointer-events-none after:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.1)]">
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                        />
                        {product.platform && (
                          <div className="absolute top-2.5 right-2.5 z-10">
                            <PlatformBadge platform={product.platform} />
                          </div>
                        )}
                        {/* Out of stock overlay */}
                        {product.stock_quantity <= 0 && (
                          <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                            <div className="bg-red-500/80 text-white px-3 py-1 rounded-md font-medium text-sm transform rotate-[-15deg]">
                              Out of Stock
                            </div>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </div>
                      <div className="relative flex flex-col flex-1 p-4">
                        <div className="flex-1">
                          <h3 className="font-medium text-white/90 text-sm line-clamp-1 mb-1">{product.name}</h3>
                          <p className="text-white/50 text-xs line-clamp-2 mb-3 group-hover:text-white/70 transition-colors">
                            {product.description}
                          </p>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-lg font-semibold text-white">£{product.price.toFixed(2)}</span>
                          <div className="flex items-center gap-2">
                            {product.stock_quantity > 0 ? (
                              <span className="text-xs text-green-400">In stock: {product.stock_quantity}</span>
                            ) : (
                              <span className="text-xs text-red-400">Out of stock</span>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                addToOrder(product);
                              }}
                              disabled={product.stock_quantity <= 0}
                              className={`px-3 py-1.5 text-white text-sm rounded-lg flex items-center gap-2 group/btn ${
                                product.stock_quantity <= 0
                                  ? 'bg-gray-600/50 cursor-not-allowed'
                                  : 'bg-purple-600/90 hover:bg-purple-600 transition-colors'
                              }`}
                            >
                              <Plus className="w-4 h-4 group-hover/btn:scale-110 transition-transform" />
                              Add
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="relative shrink-0">
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-28 h-28 object-cover rounded-lg"
                        />
                        {/* Out of stock overlay */}
                        {product.stock_quantity <= 0 && (
                          <div className="absolute inset-0 bg-black/70 flex items-center justify-center rounded-lg">
                            <div className="bg-red-500/80 text-white px-2 py-0.5 rounded-md font-medium text-xs transform rotate-[-15deg]">
                              Out of Stock
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0 flex flex-col h-full py-1">
                        <div className="flex-1">
                          <div className="flex items-start justify-between gap-4 mb-1">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <h3 className="text-white/90 font-medium text-base">{product.name}</h3>
                              {product.platform && (
                                <PlatformBadge platform={product.platform} />
                              )}
                            </div>
                            <span className="text-lg font-semibold text-white shrink-0">£{product.price.toFixed(2)}</span>
                          </div>
                          <p className="text-white/50 text-sm line-clamp-2 mb-3 group-hover:text-white/70 transition-colors">
                            {product.description}
                          </p>
                        </div>
                        <div className="flex items-center justify-between">
                          {product.stock_quantity > 0 ? (
                            <span className="text-xs text-green-400">In stock: {product.stock_quantity}</span>
                          ) : (
                            <span className="text-xs text-red-400">Out of stock</span>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              addToOrder(product);
                            }}
                            disabled={product.stock_quantity <= 0}
                            className={`self-start px-4 py-2 text-white text-sm rounded-lg flex items-center gap-2 group/btn ${
                              product.stock_quantity <= 0
                                ? 'bg-gray-600/50 cursor-not-allowed'
                                : 'bg-purple-600/90 hover:bg-purple-600 transition-colors'
                            }`}
                          >
                            <Plus className="w-4 h-4 group-hover/btn:scale-110 transition-transform" />
                            Add to Cart
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Quick View Modal */}
      {showQuickView && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-6" onClick={() => setShowQuickView(null)}>
          <div 
            className="relative w-full max-w-4xl animate-modal-pop"
            onClick={e => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setShowQuickView(null)}
              className="absolute -top-12 right-0 p-3 rounded-full bg-black/20 hover:bg-black/40 backdrop-blur-sm border border-white/10 transition-all duration-300 group z-10"
            >
              <X className="w-5 h-5 text-white/70 group-hover:text-white transition-colors" />
            </button>

            {/* Main Card */}
            <div className="relative h-[600px] rounded-2xl overflow-hidden shadow-2xl">
              {/* Background Image */}
              <div className="absolute inset-0">
                <img
                  src={showQuickView.image_url}
                  alt=""
                  className="w-full h-full object-cover blur-[2px]"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/90" />
              </div>

              {/* Content Overlay */}
              <div className="relative h-full p-12 flex flex-col">
                {/* Header */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    {showQuickView.platform && (
                      <PlatformBadge platform={showQuickView.platform} />
                    )}
                    <div className="w-px h-4 bg-white/20" />
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${showQuickView.stock_quantity > 0 ? 'bg-green-400' : 'bg-red-400'}`} />
                      {showQuickView.stock_quantity > 0 ? (
                        <span className="text-white/70 text-sm">In Stock: {showQuickView.stock_quantity}</span>
                      ) : (
                        <span className="text-red-400/70 text-sm">Out of Stock</span>
                      )}
                    </div>
                  </div>
                  <h2 className="text-5xl font-light text-white tracking-tight leading-none">{showQuickView.name}</h2>
                </div>

                {/* Description */}
                <div className="mt-8 max-w-2xl">
                  <p className="text-white/70 text-xl leading-relaxed font-light">{showQuickView.description}</p>
                </div>

                {/* Bottom Section */}
                <div className="mt-auto flex items-end justify-between">
                  {/* Price */}
                  <div className="space-y-2">
                    <div className="flex items-baseline gap-3">
                      <span className="text-5xl font-light text-white tracking-tighter">£{showQuickView.price.toFixed(2)}</span>
                      <span className="text-white/40 text-sm uppercase tracking-wider">inc. VAT</span>
                    </div>
                    <div className="text-purple-400/80 text-sm">
                      Earn {Math.floor(showQuickView.price)} points with this purchase
                    </div>
                  </div>

                  {/* Add to Cart Button */}
                  <button
                    onClick={() => {
                      addToOrder(showQuickView);
                      setShowQuickView(null);
                    }}
                    disabled={showQuickView.stock_quantity <= 0}
                    className={`px-8 py-4 rounded-xl text-white font-medium relative group overflow-hidden ${
                      showQuickView.stock_quantity <= 0
                        ? 'bg-gray-600/50 cursor-not-allowed'
                        : 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 transition-all duration-300'
                    }`}
                  >
                    {/* Button shine effect - only show for in-stock items */}
                    {showQuickView.stock_quantity > 0 && (
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                    )}
                    
                    {/* Button content */}
                    <div className="relative flex items-center gap-3">
                      <Plus className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
                      <span className="text-base tracking-wide">
                        {showQuickView.stock_quantity > 0 ? 'Add to Cart' : 'Out of Stock'}
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Order Confirmation Modal */}
      {showConfirmation && currentOrderId && (
        <OrderConfirmation
          isOpen={showConfirmation}
          onClose={() => {
            setShowConfirmation(false);
            setOrderItems([]);
            setApplyingPoints(false);
            setPointsToApply(0);
            setCurrentOrderId(null);
            // Refresh product data when closing the confirmation modal
            fetchProducts();
            // Refresh visionary data to show updated points
            refreshVisionaryData();
          }}
          orderItems={orderItems}
          orderId={currentOrderId}
          visionary={visionary}
          pointsUsed={pointsToApply}
          pointsEarned={Math.floor(total)}
          total={total}
        />
      )}

      {showPaymentModal && paymentClientSecret && (
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret: paymentClientSecret,
            appearance: {
              theme: 'night',
              variables: {
                colorPrimary: '#9333ea',
                colorBackground: '#000000',
                colorText: '#ffffff'
              }
            }
          }}
        >
          <PaymentModal
            isOpen={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            clientSecret={paymentClientSecret}
            orderItems={orderItems}
            visionary={visionary}
            pointsToApply={pointsToApply}
            onSuccess={handlePaymentSuccess}
          />
        </Elements>
      )}
    </>
  );
} 
